import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import {
  Cake, CalendarDays, PalmtreeIcon, MessagesSquare, Sparkles, MapPin,
  Link2, GraduationCap, Car, AlertTriangle, CheckCircle2, ShieldAlert,
  Wrench, ArrowRight, Search, Command,
} from "lucide-react";
import DagoverzichtWidget from "@/components/DagoverzichtWidget";
import AIUsageWidget from "@/components/AIUsageWidget";
import AIUsageTrendChart from "@/components/AIUsageTrendChart";
import SignaleringenWidget from "@/components/SignaleringenWidget";
import ContractAlertsWidget from "@/components/ContractAlertsWidget";
import SyncStatusPaneel from "@/components/SyncStatusPaneel";
import AiBudgetBanner from "@/components/AiBudgetBanner";
import WorkdriveImportKaart from "@/components/WorkdriveImportKaart";
import { DashboardIndelingKnop, DashboardStandaardKnop, MeerTonenKnop, useDashboardIndeling } from "@/components/DashboardIndeling";
import { isManager, isManagement, MANAGERS, MANAGEMENT, heeftRol } from "@/lib/rollen";
import { WerkbordPaneel } from "@/components/vandaag/WerkbordPaneel";
import MijnBijeenkomsten from "@/components/bijeenkomsten/MijnBijeenkomsten";
import { NaadloosDashboardKaart } from "@/components/klachten/naadloos/NaadloosDashboardKaart";

/* --------------------------------------------------------------------------
 * Dashboard — herstructurering conform /app/design_guidelines.json
 *  Layout:
 *    • Hero-strook met begroeting + Cmd-K hint
 *    • Grid 12 kolommen:
 *        - Main col 8 : KPI-tiles + Dagoverzicht + Activity-feed
 *        - Right col 4: AI-usage + Verjaardagen + Vergader-reminders
 * -------------------------------------------------------------------------- */

function MijnTakenWidget({ nav }) {
  const [taken, setTaken] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/mijn-taken");
        setTaken(data.taken || []);
      } catch {}
      setLoading(false);
    })();
  }, []);
  if (loading) return null;
  if (!taken.length) return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="mijn-taken-leeg">
      <div className="flex items-center gap-2">
        <span className="text-lg">🎉</span>
        <span className="text-sm font-semibold text-emerald-800">Alles opgeruimd — geen openstaande taken voor jou.</span>
      </div>
    </div>
  );
  return (
    <div className="bg-white border border-blue-300 rounded-md p-3 shadow-sm" data-testid="mijn-taken-widget">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">📌</span>
        <span className="text-xs font-bold uppercase tracking-widest text-blue-800">Wat moet ik nu doen</span>
        <span className="text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full">{taken.length}</span>
      </div>
      <div className="space-y-1.5">
        {taken.slice(0, 6).map(t => (
          <button
            key={t.id}
            onClick={() => nav(t.link)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs transition ${
              t.prioriteit === "hoog"
                ? "bg-rose-50 hover:bg-rose-100 border-l-2 border-rose-500"
                : "bg-slate-50 hover:bg-slate-100 border-l-2 border-slate-300"
            }`}
            data-testid={`mijn-taak-${t.id}`}
          >
            <span className="text-lg leading-none">{t.icon}</span>
            <span className="flex-1">{t.titel}</span>
            <ArrowRight className="h-3 w-3 opacity-60" />
          </button>
        ))}
      </div>
    </div>
  );
}

function AandachtStrook({ nav, isMgr, isSyncBeheerder, canRead }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = [];
      if (isSyncBeheerder) {
        try {
          const { data: sh } = await api.get("/sync-health").catch(() => ({ data: null }));
          if (sh && sh.overall_status === "rood") {
            const rood = (sh.koppelingen || []).filter((k) => k.status === "rood").map((k) => k.naam).join(", ");
            results.push({ id: "sync", label: `Sync-storing: ${rood || "koppeling"}`, tone: "rose", link: "/sync-health" });
          }
        } catch {}
      }
      if (canRead("Tickets")) {
        try {
          const { data } = await api.get("/tickets", { params: { status: "open" } }).catch(() => ({ data: [] }));
          const openN = Array.isArray(data) ? data.length : 0;
          if (openN > 0) results.push({ id: "tickets", label: `${openN} open ticket${openN === 1 ? "" : "s"}`, tone: "amber", link: "/tickets" });
        } catch {}
      }
      if (isMgr) {
        try {
          const { data } = await api.get("/vaste-klanten/dashboard").catch(() => ({ data: null }));
          const kg = data?.actie_nodig || 0;
          if (kg > 0) results.push({ id: "vk", label: `${kg} vaste klanten hebben actie nodig`, tone: "rose", link: "/vaste-klanten" });
        } catch {}
        try {
          const { data } = await api.get("/bestellingen/te-verwerken/dashboard").catch(() => ({ data: null }));
          const b = data?.openstaand || 0;
          if (b > 0) results.push({ id: "best", label: `${b} openstaande bestellingen`, tone: "amber", link: "/bestellingen?tab=te-verwerken" });
        } catch {}
      }
      if (!cancelled) setItems(results);
    })();
    return () => { cancelled = true; };
  }, [isMgr, isSyncBeheerder, canRead]);

  if (!items.length) return null;
  return (
    <div className="bg-gradient-to-r from-rose-50/70 to-amber-50/70 border border-rose-200 rounded-md p-3 shadow-none" data-testid="aandacht-strook">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="h-4 w-4 text-rose-700" />
        <span className="text-xs font-bold uppercase tracking-widest text-rose-800">Aandacht nodig</span>
        <span className="text-[10px] text-rose-700">{items.length} punt{items.length !== 1 ? "en" : ""}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <button
            key={it.id}
            onClick={() => nav(it.link)}
            className={`text-xs px-3 py-1.5 rounded-full border font-medium transition ${
              it.tone === "rose"
                ? "bg-rose-100 border-rose-200 text-rose-800 hover:bg-rose-200"
                : "bg-amber-100 border-amber-200 text-amber-800 hover:bg-amber-200"
            }`}
            data-testid={`aandacht-${it.id}`}
          >
            {it.label} <ArrowRight className="inline h-3 w-3 ml-1" />
          </button>
        ))}
      </div>
    </div>
  );
}


function KpiTile({ label, value, icon: Icon, tone = "slate", onClick, testid }) {
  const tones = {
    slate:   "bg-white border-border text-slate-900",
    blue:    "bg-white border-border text-slate-900",
    emerald: "bg-emerald-50/60 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50/60 border-amber-200 text-amber-800",
    rose:    "bg-rose-50/60 border-rose-200 text-rose-800",
  };
  const iconTones = {
    slate:   "bg-slate-100 text-slate-600",
    blue:    "bg-[hsl(var(--brand-blue))]/10 text-[hsl(var(--brand-blue))]",
    emerald: "bg-emerald-100 text-emerald-700",
    amber:   "bg-amber-100 text-amber-700",
    rose:    "bg-rose-100 text-rose-700",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left border ${tones[tone]} rounded-md p-4 transition-colors hover:border-slate-400 focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:outline-none`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
        <span className={`h-8 w-8 rounded-md flex items-center justify-center ${iconTones[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-2 font-heading text-3xl font-bold tracking-tight tabular-nums">{value}</div>
    </button>
  );
}

function SectionCard({ title, action, children, testid, dense = false }) {
  return (
    <Card
      className={`bg-white border-border shadow-none ${dense ? "p-4" : "p-5"}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-base font-semibold tracking-tight text-slate-900">{title}</h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-sm text-slate-400 flex items-center gap-2 py-6">
      <Icon className="h-4 w-4" /> {text}
    </div>
  );
}

export default function Dashboard() {
  const { user, canRead } = useAuth();
  const isMgr = isManager(user);
  const isSyncBeheerder = heeftRol(user, ["manager_plus", "manager", "teammanager"]);
  const [meetings, setMeetings] = useState([]);
  const [verlof, setVerlof] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [taken, setTaken] = useState(null);
  const [calc, setCalc] = useState(null);
  const nav = useNavigate();
  // Blokken die deze rol überhaupt kan zien (zelfde volgorde als DASHBOARD_SECTIES) — bepaalt 'eerste 6' en 'Meer tonen'
  const beschikbaar = useMemo(() => [
    "kpi", "aandacht", isMgr && "signaleringen", isSyncBeheerder && "sync", isMgr && "contracten", "mijntaken", "dagoverzicht",
    canRead("Vergaderingen") && "vergaderingen", canRead("Tickets") && "tickets", canRead("Verlof") && "verlof", canRead("Taken") && "taken",
    canRead("Assets") && "assets", isMgr && "aiusage", "verjaardagen", canRead("Vergaderingen") && "herinneringen", "snelkoppelingen", isMgr && "aitrend",
  ].filter(Boolean), [isMgr, isSyncBeheerder, canRead]);
  const { indeling, setIndeling, zichtbaar, meerOpen, setMeerOpen, achterMeer, standaardSet } = useDashboardIndeling(beschikbaar);
  const [standaardVersie, setStandaardVersie] = useState(0);
  useEffect(() => {
    if (!standaardVersie) return;
    api.get("/me/dashboard-indeling").then(({ data }) => setIndeling({ verborgen: data.verborgen || [], getoond: data.getoond || [], compact: !!data.compact, standaard: data.standaard ?? null })).catch(() => {});
  }, [standaardVersie, setIndeling]);

  useEffect(() => {
    (async () => {
      try {
        const promises = [];
        promises.push(canRead("Vergaderingen") ? api.get("/vergaderingen") : Promise.resolve({ data: [] }));
        promises.push(canRead("Verlof") ? api.get("/verlof") : Promise.resolve({ data: [] }));
        promises.push(api.get("/birthdays").catch(() => ({ data: [] })));
        promises.push(canRead("Tickets") ? api.get("/tickets") : Promise.resolve({ data: [] }));
        promises.push(canRead("Assets") ? api.get("/assets").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }));
        promises.push(canRead("Vergaderingen") ? api.get("/vergaderingen/herinneringen").catch(() => ({ data: { reminders: [] } })) : Promise.resolve({ data: { reminders: [] } }));
        promises.push(canRead("Taken") ? api.get("/taken/mijn-vandaag").catch(() => ({ data: null })) : Promise.resolve({ data: null }));
        const [m, v, b, t, a, r, tk] = await Promise.all(promises);
        setMeetings(m.data); setVerlof(v.data); setBirthdays(b.data); setTickets(t.data);
        setAssets(a.data);
        setReminders(r.data.reminders || []);
        setTaken(tk.data);
        if (!isMgr && user?.employee_id && canRead("Verlof")) {
          const { data: c } = await api.get(`/verlof/calculator/${user.employee_id}`);
          setCalc(c);
        }
      } catch { /* silent */ }
    })();
  }, [user, isMgr, canRead]);

  const today = new Date().toISOString().slice(0, 10);
  const in30Days = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  const upcoming = useMemo(() => meetings.filter((m) => m.datum >= today).slice(0, 5), [meetings, today]);
  const openTickets = useMemo(() => tickets.filter((t) => t.status !== "gesloten").slice(0, 5), [tickets]);
  const bdToday = useMemo(() => birthdays.filter((b) => b.is_vandaag), [birthdays]);
  const bdMonth = useMemo(() => birthdays.filter((b) => b.is_deze_maand && !b.is_vandaag).slice(0, 5), [birthdays]);

  // Assets stats
  const totalAssets = assets.length;
  const gekeurd = assets.filter(a => a.laatst_gekeurd && a.volgende_keuring && a.volgende_keuring > today).length;
  const verlopen = assets.filter(a => a.volgende_keuring && a.volgende_keuring < today).length;
  const binnenkort = assets.filter(a => a.volgende_keuring && a.volgende_keuring >= today && a.volgende_keuring <= in30Days).length;

  const openTicketsCount = tickets.filter((t) => t.status !== "gesloten").length;
  const verlofAangevraagd = verlof.filter((v) => v.status === "aangevraagd").length;
  const takenVandaagCount = (taken?.vandaag?.length || 0) + (taken?.overtijd?.length || 0);

  return (
    <div className={indeling.compact ? "space-y-3" : "space-y-6"} data-testid="dashboard-page">
      <WerkbordPaneel />
      {/* ============================================================
          HERO — begroeting + zoek-hint
      ============================================================ */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Welkom terug</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">
            {user?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isMgr ? "Manager-overzicht — alles op één plek" : "Persoonlijk overzicht"}
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
        <button
          onClick={() => window.dispatchEvent(new Event("bpt:open-cmdk"))}
          data-testid="dashboard-cmdk-hint"
          className="group flex-1 sm:w-[340px] lg:w-[420px] inline-flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-white text-slate-500 hover:border-[hsl(var(--brand-blue))]/60 hover:shadow-md transition-all text-sm text-left min-w-0"
        >
          <Search className="h-4 w-4 text-slate-400 group-hover:text-[hsl(var(--brand-blue))] shrink-0" />
          <span className="flex-1 truncate">Zoek klanten, werkbonnen, contracten, tickets…</span>
          <kbd className="ml-2 hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 border border-border text-[10px] font-mono text-slate-500">
            <Command className="h-3 w-3" /> K
          </kbd>
        </button>
        {user?.role === "manager_plus" && <DashboardStandaardKnop onGewijzigd={() => setStandaardVersie((v) => v + 1)} />}
        <DashboardIndelingKnop indeling={indeling} onChange={setIndeling} beschikbaar={beschikbaar} standaardSet={standaardSet} />
        </div>
      </div>

      <AiBudgetBanner />
      {isMgr && <WorkdriveImportKaart />}
      {(isMgr || heeftRol(user, ["kcc", "salesteam"])) && <NaadloosDashboardKaart />}

      {/* ============================================================
          KPI-strook (top row) — 4 tiles
      ============================================================ */}
      {zichtbaar("kpi") && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {canRead("Tickets") && (
          <KpiTile
            label="Open tickets"
            value={openTicketsCount}
            icon={MessagesSquare}
            tone={openTicketsCount > 0 ? "amber" : "slate"}
            onClick={() => nav("/tickets")}
            testid="kpi-tickets"
          />
        )}
        {canRead("Verlof") && (
          <KpiTile
            label={isMgr ? "Verlof aanvragen" : "Verlof resterend"}
            value={isMgr ? verlofAangevraagd : (calc?.resterend ?? "—")}
            icon={PalmtreeIcon}
            tone={isMgr && verlofAangevraagd > 0 ? "amber" : "emerald"}
            onClick={() => nav("/verlof")}
            testid="kpi-verlof"
          />
        )}
        {canRead("Taken") && (
          <KpiTile
            label="Taken vandaag"
            value={takenVandaagCount}
            icon={CheckCircle2}
            tone={(taken?.overtijd?.length || 0) > 0 ? "rose" : "blue"}
            onClick={() => nav("/taken")}
            testid="kpi-taken"
          />
        )}
        {canRead("Assets") && totalAssets > 0 && (
          <KpiTile
            label="Assets · verlopen"
            value={verlopen}
            icon={ShieldAlert}
            tone={verlopen > 0 ? "rose" : "slate"}
            onClick={() => nav("/assets?filter=verlopen")}
            testid="kpi-assets-verlopen"
          />
        )}
      </div>
      )}

      {/* P1 — Aandachtspunten strook (rood/oranje badges met call-to-action) */}
      {zichtbaar("aandacht") && <AandachtStrook nav={nav} isMgr={isMgr} isSyncBeheerder={isSyncBeheerder} canRead={canRead} />}

      {/* Signaleringen: alle cross-beheer signalen gebundeld (alleen managers) */}
      {zichtbaar("signaleringen") && <SignaleringenWidget />}

      {/* Sync-status alleen voor rollen die syncs kunnen beheren (directie/ICT) */}
      {isSyncBeheerder && zichtbaar("sync") && <SyncStatusPaneel />}
      {isMgr && zichtbaar("contracten") && <ContractAlertsWidget nav={nav} />}

      {/* Persoonlijke Wat-moet-ik-nu-doen widget (per rol) */}
      {zichtbaar("mijntaken") && <MijnTakenWidget nav={nav} />}

      {/* ============================================================
          Birthday banner (vandaag) — full width
      ============================================================ */}
      {bdToday.length > 0 && (
        <Card className="p-4 bg-[hsl(var(--brand-orange))]/5 border-[hsl(var(--brand-orange))]/30 shadow-none" data-testid="birthday-banner">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[hsl(var(--brand-orange))] text-white flex items-center justify-center shrink-0">
              <Cake className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[hsl(var(--brand-orange))]">Vandaag jarig</div>
              <div className="font-heading text-base font-semibold tracking-tight truncate">
                {bdToday.map((b) => b.naam).join(", ")} — vergeet niet te feliciteren!
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ============================================================
          MAIN GRID — 12 kolommen · main (8) + right rail (4)
      ============================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* ---------------- LEFT / MAIN COLUMN ---------------- */}
        <div className="lg:col-span-8 space-y-4">
          <MijnBijeenkomsten />
          {/* Dagoverzicht widget bevat interne Vandaag/Morgen/Deze week tabs */}
          {zichtbaar("dagoverzicht") && <DagoverzichtWidget />}

          {/* Aankomende vergaderingen + Openstaande tickets naast elkaar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {canRead("Vergaderingen") && zichtbaar("vergaderingen") && (
              <SectionCard
                title="Aankomende vergaderingen"
                testid="upcoming-card"
                action={
                  <button
                    onClick={() => nav("/vergaderingen")}
                    className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid="upcoming-all"
                  >
                    Alles <ArrowRight className="h-3 w-3" />
                  </button>
                }
              >
                {upcoming.length === 0 ? (
                  <EmptyState icon={CalendarDays} text="Geen aankomende items." />
                ) : (
                  <ul className="divide-y divide-border">
                    {upcoming.map((m) => (
                      <li key={m.id} className="py-2.5 flex items-center gap-3">
                        <div className="w-11 shrink-0 text-center">
                          <div className="font-heading text-lg font-bold tracking-tight tabular-nums leading-none">
                            {m.datum?.slice(8, 10)}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5 tabular-nums">
                            {m.datum?.slice(5, 7)}·{m.datum?.slice(0, 4)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate text-slate-900">{m.titel}</div>
                          <div className="text-xs text-slate-500 truncate">
                            <span className="uppercase tracking-wider text-[10px]">{m.type}</span>
                            {m.locatie ? ` · ${m.locatie}` : ""}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            )}

            {canRead("Tickets") && zichtbaar("tickets") && (
              <SectionCard
                title="Openstaande tickets"
                testid="tickets-card"
                action={
                  <button
                    onClick={() => nav("/tickets")}
                    className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid="tickets-all"
                  >
                    Alles <ArrowRight className="h-3 w-3" />
                  </button>
                }
              >
                {openTickets.length === 0 ? (
                  <EmptyState icon={MessagesSquare} text="Geen open tickets." />
                ) : (
                  <ul className="divide-y divide-border">
                    {openTickets.map((t) => (
                      <li key={t.id} className="py-2.5 cursor-pointer hover:bg-slate-50 rounded transition-colors -mx-2 px-2" onClick={() => nav("/tickets")}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[11px] font-mono text-slate-400 tabular-nums">{t.ticket_nummer}</div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold ${
                            t.prioriteit === "hoog" ? "bg-rose-100 text-rose-700"
                              : t.prioriteit === "midden" ? "bg-amber-100 text-amber-700"
                              : "bg-slate-100 text-slate-600"
                          }`}>{t.status}</span>
                        </div>
                        <div className="text-sm font-medium truncate text-slate-900">{t.onderwerp}</div>
                        <div className="text-xs text-slate-500 truncate">van {t.aanmaker_naam}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            )}
          </div>

          {/* Verlof + Taken naast elkaar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {canRead("Verlof") && zichtbaar("verlof") && (
              <SectionCard
                title="Recente verlofaanvragen"
                testid="verlof-card"
                action={
                  <button
                    onClick={() => nav("/verlof")}
                    className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid="verlof-all"
                  >
                    Alles <ArrowRight className="h-3 w-3" />
                  </button>
                }
              >
                {verlof.length === 0 ? (
                  <EmptyState icon={PalmtreeIcon} text="Geen verlofaanvragen." />
                ) : (
                  <ul className="divide-y divide-border">
                    {verlof.slice(0, 5).map((v) => (
                      <li key={v.id} className="py-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium text-slate-900">{v.aantal_dagen} dagen</div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold ${
                            v.status === "goedgekeurd" ? "bg-emerald-100 text-emerald-700"
                              : v.status === "afgewezen" ? "bg-rose-100 text-rose-700"
                              : "bg-amber-100 text-amber-700"
                          }`}>{v.status}</span>
                        </div>
                        <div className="text-xs text-slate-500 tabular-nums">{v.start_datum} → {v.eind_datum}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            )}

            {canRead("Taken") && zichtbaar("taken") && taken && (taken.vandaag?.length > 0 || taken.overtijd?.length > 0) && (
              <SectionCard
                title={`Mijn taken vandaag (${taken.vandaag?.length || 0})`}
                testid="taken-card"
                action={
                  <button
                    onClick={() => nav("/taken")}
                    className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid="taken-all"
                  >
                    Alles <ArrowRight className="h-3 w-3" />
                  </button>
                }
              >
                {taken.overtijd?.length > 0 && (
                  <div className="mb-2 px-2.5 py-1.5 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800 flex items-center gap-2">
                    <ShieldAlert className="h-3.5 w-3.5" /> {taken.overtijd.length} taak/taken overtijd
                  </div>
                )}
                {(taken.vandaag || []).length === 0 ? (
                  <EmptyState icon={CheckCircle2} text="Geen taken vandaag." />
                ) : (
                  <ul className="divide-y divide-border">
                    {(taken.vandaag || []).slice(0, 5).map((t) => (
                      <li key={t.id} className="py-2.5 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 rounded transition-colors -mx-2 px-2" onClick={() => nav("/taken")}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate text-slate-900">{t.titel}</div>
                          <div className="text-xs text-slate-500 tabular-nums">{t.geplande_tijd} · {t.duur_minuten}m · {t.categorie}</div>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold ${
                          t.status === "in_uitvoering" ? "bg-orange-100 text-orange-700"
                            : t.status === "verzet" ? "bg-amber-100 text-amber-700"
                            : "bg-blue-100 text-blue-700"
                        }`}>{t.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            )}
          </div>

          {/* Assets keuringsstatus (compact) — alleen als beheerder + data */}
          {canRead("Assets") && zichtbaar("assets") && totalAssets > 0 && (
            <SectionCard
              title="Assets · keuringsstatus"
              testid="assets-status-card"
              action={
                <button
                  onClick={() => nav("/assets")}
                  className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                  data-testid="assets-all"
                >
                  Alles <ArrowRight className="h-3 w-3" />
                </button>
              }
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button onClick={() => nav("/assets")} className="text-left rounded-md border border-border p-3 hover:border-slate-400 transition-colors" data-testid="asset-tile-total">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Car className="h-3.5 w-3.5" /> <span className="text-[10px] font-semibold uppercase tracking-widest">Totaal</span>
                  </div>
                  <div className="font-heading text-2xl font-bold tracking-tight mt-1 tabular-nums">{totalAssets}</div>
                </button>
                <button onClick={() => nav("/assets?filter=gekeurd")} className="text-left rounded-md border border-emerald-200 bg-emerald-50/60 p-3 hover:border-emerald-400 transition-colors" data-testid="asset-tile-gekeurd">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" /> <span className="text-[10px] font-semibold uppercase tracking-widest">Actueel</span>
                  </div>
                  <div className="font-heading text-2xl font-bold tracking-tight mt-1 text-emerald-700 tabular-nums">{gekeurd}</div>
                </button>
                <button onClick={() => nav("/assets?filter=binnenkort")} className={`text-left rounded-md border ${binnenkort > 0 ? "border-amber-300 ring-1 ring-amber-200" : "border-amber-200"} bg-amber-50/60 p-3 hover:border-amber-400 transition-colors`} data-testid="asset-tile-binnenkort">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5" /> <span className="text-[10px] font-semibold uppercase tracking-widest">≤ 30 dgn</span>
                  </div>
                  <div className="font-heading text-2xl font-bold tracking-tight mt-1 text-amber-700 tabular-nums">{binnenkort}</div>
                </button>
                <button onClick={() => nav("/assets?filter=verlopen")} className={`text-left rounded-md border ${verlopen > 0 ? "border-rose-300 ring-1 ring-rose-200" : "border-rose-200"} bg-rose-50/60 p-3 hover:border-rose-400 transition-colors`} data-testid="asset-tile-verlopen">
                  <div className="flex items-center gap-2 text-rose-700">
                    <ShieldAlert className="h-3.5 w-3.5" /> <span className="text-[10px] font-semibold uppercase tracking-widest">Verlopen</span>
                  </div>
                  <div className="font-heading text-2xl font-bold tracking-tight mt-1 text-rose-700 tabular-nums">{verlopen}</div>
                </button>
              </div>
            </SectionCard>
          )}
        </div>

        {/* ---------------- RIGHT RAIL ---------------- */}
        <aside className="lg:col-span-4 space-y-4">
          {/* AI-usage widget — alleen managers */}
          {isMgr && zichtbaar("aiusage") && <AIUsageWidget />}

          {/* Verjaardagen deze maand */}
          {zichtbaar("verjaardagen") && (
          <SectionCard title="Verjaardagen deze maand" testid="birthdays-card" dense>
            {bdMonth.length === 0 && bdToday.length === 0 ? (
              <EmptyState icon={Cake} text="Geen verjaardagen." />
            ) : (
              <ul className="divide-y divide-border">
                {bdToday.concat(bdMonth).map((b) => (
                  <li key={b.id} className="py-2 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate text-slate-900">{b.naam}</div>
                      <div className="text-xs text-slate-500 truncate">{b.functie}</div>
                    </div>
                    <div className={`text-xs font-mono tabular-nums ${b.is_vandaag ? "text-[hsl(var(--brand-orange))] font-bold" : "text-slate-500"}`}>{b.dd_mm}</div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
          )}

          {/* Vergader-herinneringen */}
          {reminders.length > 0 && zichtbaar("herinneringen") && (
            <SectionCard title={`Vergader-herinneringen (${reminders.length})`} testid="vergad-reminders" dense>
              <ul className="space-y-1.5">
                {reminders.slice(0, 6).map((r, i) => (
                  <li
                    key={i}
                    className="text-xs cursor-pointer hover:bg-slate-50 rounded px-2 py-1.5 transition-colors -mx-2"
                    onClick={() => nav("/vergaderingen")}
                  >
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] mr-2 uppercase tracking-wider font-semibold ${
                      r.level === "critical" ? "bg-rose-100 text-rose-700"
                        : r.level === "warning" ? "bg-amber-100 text-amber-800"
                        : "bg-slate-100 text-slate-700"
                    }`}>{r.type}</span>
                    <span className="text-slate-700">{r.bericht}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}

          {/* Snelkoppelingen — compact 2-koloms grid */}
          {zichtbaar("snelkoppelingen") && (
          <SectionCard title="Snelkoppelingen" testid="quick-tiles-card" dense>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "AI Assistent", icon: Sparkles, to: "/ai-assistent", module: "AI Assistent", accent: "text-[hsl(var(--brand-blue))]" },
                { label: "Werkbonnen", icon: Wrench, to: "/werkbonnen", module: "Werkbonnen", accent: "text-slate-700" },
                { label: "Training", icon: GraduationCap, to: "/cursussen", module: "Training", accent: "text-emerald-700" },
                { label: "Snelkoppelingen", icon: Link2, to: "/snelkoppelingen", module: "Snelkoppelingen", accent: "text-slate-700" },
                { label: "Vestigingen", icon: MapPin, to: "/locaties", module: "Vestigingen", accent: "text-slate-700" },
              ]
                .filter((t) => canRead(t.module))
                .map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.to}
                      onClick={() => nav(t.to)}
                      data-testid={`tile-${t.to.replace("/", "")}`}
                      className="text-left border border-border rounded-md p-3 hover:border-slate-400 transition-colors bg-white"
                    >
                      <Icon className={`h-4 w-4 ${t.accent}`} />
                      <div className="mt-2 text-xs font-semibold text-slate-900 truncate">{t.label}</div>
                    </button>
                  );
                })}
            </div>
          </SectionCard>
          )}
        </aside>
      </div>

      {/* ============================================================
          AI-usage trend (full-width onderin, alleen zichtbaar voor managers via server)
      ============================================================ */}
      {isMgr && zichtbaar("aitrend") && <AIUsageTrendChart dagen={14} />}

      <MeerTonenKnop achterMeer={achterMeer} meerOpen={meerOpen} setMeerOpen={setMeerOpen} />
    </div>
  );
}
